import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { salesApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Pipeline forecast — FR-SALES-04.05
 * Weighted pipeline by stage and by assignee, with period win/loss stats.
 */
export function PipelinePage() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const q = useQuery({
    queryKey: ['pipeline', fromDate, toDate],
    queryFn: () => salesApi.getPipelineForecast({
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
  });

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Pipeline forecast"
        subtitle="Weighted pipeline by stage and rep. Use the date range to scope won/lost."
      />

      {/* Date filter */}
      <div className="card p-4 mb-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">Close date from</label>
          <input type="date" className="input w-44" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-44" value={toDate}
            onChange={(e) => setToDate(e.target.value)} />
        </div>
        {(fromDate || toDate) && (
          <button className="btn-secondary"
            onClick={() => { setFromDate(''); setToDate(''); }}>
            Clear
          </button>
        )}
      </div>

      {q.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : (
        <>
          {/* KPI strip — the weighted forecast is the hero */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <KpiTile label="Open leads" value={q.data.summary.openCount} />
            <KpiTile label="Pipeline value" value={fmtMoney(q.data.summary.totalOpenValue)} />
            <KpiTile
              label="Weighted forecast"
              value={fmtMoney(q.data.summary.weightedForecast)}
              accent="sage"
              emphasize
            />
            <KpiTile label="Won (period)" value={fmtMoney(q.data.summary.wonValue)}
              accent="sage"
              sublabel={`${q.data.summary.wonCount} deals`} />
            <KpiTile label="Lost (period)" value={fmtMoney(q.data.summary.lostValue)}
              accent={q.data.summary.lostCount > 0 ? 'danger' : 'default'}
              sublabel={`${q.data.summary.lostCount} deals`} />
            <KpiTile label="Win rate"
              value={`${q.data.summary.winRate}%`}
              accent={q.data.summary.winRate >= 50 ? 'sage' : 'default'} />
          </div>

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-ink-100">
                <div className="eyebrow text-ink-500">By stage</div>
                <div className="font-semibold text-ink-900 mt-0.5">Pipeline stages</div>
              </div>
              {q.data.byStage.length === 0 ? (
                <EmptyState message="No open leads in pipeline" hint="Create leads to populate the funnel." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Value</th>
                        <th className="text-right">Weighted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.data?.byStage ?? []).map((s: any) => (
                        <tr key={s.stage}>
                          <td className="capitalize text-sm font-medium text-ink-900">{s.stage}</td>
                          <td className="text-right font-mono text-sm text-ink-700 tabular-nums">{s.leadCount}</td>
                          <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                            ${s.totalValue.toFixed(0)}
                          </td>
                          <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                            ${s.weightedValue.toFixed(0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-ink-100">
                <div className="eyebrow text-ink-500">By assignee</div>
                <div className="font-semibold text-ink-900 mt-0.5">Sales reps</div>
              </div>
              {q.data.byAssignee.length === 0 ? (
                <EmptyState message="No assignments" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Rep</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Value</th>
                        <th className="text-right">Weighted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(q.data?.byAssignee ?? []).map((a: any) => (
                        <tr key={a.userId ?? 'unassigned'}>
                          <td>
                            <div className="flex items-center gap-3">
                              {a.userId ? (
                                <RepAvatar name={a.name} />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-canvas-subtle text-ink-400 flex items-center justify-center text-xs flex-shrink-0">?</div>
                              )}
                              <div className={a.userId ? 'font-medium text-ink-900' : 'italic text-ink-500'}>
                                {a.name}
                              </div>
                            </div>
                          </td>
                          <td className="text-right font-mono text-sm text-ink-700 tabular-nums">{a.leadCount}</td>
                          <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                            ${a.totalValue.toFixed(0)}
                          </td>
                          <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                            ${a.weightedValue.toFixed(0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RepAvatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = [
    'bg-sage-100 text-sage-700',
    'bg-amber-100 text-amber-800',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-rose-100 text-rose-700',
    'bg-teal-100 text-teal-700',
  ];
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-2xs font-semibold flex-shrink-0 ${tones[hash % tones.length]}`}>
      {initials}
    </div>
  );
}


function KpiTile({
  label, value, accent = 'default', emphasize, sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
  emphasize?: boolean;
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-4 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className={`mt-2 font-bold font-display text-ink-900 tabular-nums ${emphasize ? 'text-2xl' : 'text-xl'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-ink-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
